import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiDelete } from '../services/api';
import {
  Plus,
  Trash2,
  Edit,
  X,
} from 'lucide-react';

interface Category {
  id: string;
  name: string;
  category_type: string;
  color: string | null;
  parent_id: string | null;
  is_active: boolean;
}

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category_type: 'expense',
    color: '#3B82F6',
  });

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<Category[]>('/api/categories');
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        // Update would need apiPut implementation
        console.log('Update not implemented yet');
      } else {
        await apiPost('/api/categories', formData);
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', category_type: 'expense', color: '#3B82F6' });
      fetchCategories();
    } catch (err) {
      console.error('Failed to save category:', err);
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await apiDelete(`/api/categories/${categoryId}`);
      fetchCategories();
    } catch (err) {
      console.error('Failed to delete category:', err);
    }
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      category_type: category.category_type,
      color: category.color || '#3B82F6',
    });
    setShowModal(true);
  };

  const expenseCategories = categories.filter(
    (c) => c.category_type === 'expense' || c.category_type === 'both'
  );
  const incomeCategories = categories.filter(
    (c) => c.category_type === 'income' || c.category_type === 'both'
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Categories</h1>
          <p className="text-gray-400">Manage expense and income categories</p>
        </div>
        <button
          onClick={() => {
            setEditingCategory(null);
            setFormData({ name: '', category_type: 'expense', color: '#3B82F6' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Category
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          {/* Expense Categories */}
          <div className="bg-dark-900 rounded-xl border border-dark-800">
            <div className="p-4 border-b border-dark-800">
              <h2 className="font-semibold text-white">Expense Categories</h2>
            </div>
            <div className="divide-y divide-dark-800">
              {expenseCategories.length === 0 ? (
                <p className="p-4 text-gray-500 text-center">No expense categories</p>
              ) : (
                expenseCategories.map((category) => (
                  <div
                    key={category.id}
                    className={`p-4 flex items-center justify-between ${
                      !category.is_active ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: category.color || '#6B7280' }}
                      />
                      <span className="text-white">{category.name}</span>
                      {!category.is_active && (
                        <span className="text-xs text-gray-500">(Inactive)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(category)}
                        className="p-1 hover:bg-dark-700 rounded text-gray-400"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="p-1 hover:bg-dark-700 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Income Categories */}
          <div className="bg-dark-900 rounded-xl border border-dark-800">
            <div className="p-4 border-b border-dark-800">
              <h2 className="font-semibold text-white">Income Categories</h2>
            </div>
            <div className="divide-y divide-dark-800">
              {incomeCategories.length === 0 ? (
                <p className="p-4 text-gray-500 text-center">No income categories</p>
              ) : (
                incomeCategories.map((category) => (
                  <div
                    key={category.id}
                    className={`p-4 flex items-center justify-between ${
                      !category.is_active ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: category.color || '#6B7280' }}
                      />
                      <span className="text-white">{category.name}</span>
                      {!category.is_active && (
                        <span className="text-xs text-gray-500">(Inactive)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(category)}
                        className="p-1 hover:bg-dark-700 rounded text-gray-400"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="p-1 hover:bg-dark-700 rounded text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-900 rounded-xl border border-dark-800 w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-dark-800">
              <h2 className="font-semibold text-white">
                {editingCategory ? 'Edit Category' : 'New Category'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Type *
                </label>
                <select
                  value={formData.category_type}
                  onChange={(e) => setFormData({ ...formData, category_type: e.target.value })}
                  className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1 px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
